import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Plus, FileText, Link as LinkIcon, Video, BookOpen, Search, Share2, Trash2, ExternalLink, FolderOpen } from "lucide-react";

interface ResourceLibraryProps {
  clinicianId: string;
  clients: any[];
}

interface Resource {
  id: string;
  title: string;
  description: string;
  resource_type: string;
  file_url: string;
  category: string;
  tags: string[];
  is_public: boolean;
  created_at: string;
}

const RESOURCE_TYPES = [
  { value: "article", label: "Article", icon: FileText },
  { value: "video", label: "Video", icon: Video },
  { value: "worksheet", label: "Worksheet", icon: BookOpen },
  { value: "link", label: "External Link", icon: LinkIcon },
];

const CATEGORIES = [
  "Anxiety",
  "Depression",
  "Trauma",
  "Relationships",
  "Mindfulness",
  "Coping Skills",
  "Self-Care",
  "CBT",
  "DBT",
  "General"
];

export function ResourceLibrary({ clinicianId, clients }: ResourceLibraryProps) {
  const { toast } = useToast();
  const [resources, setResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [selectedClientId, setSelectedClientId] = useState<string>("");
  const [shareMessage, setShareMessage] = useState("");

  const [newResource, setNewResource] = useState({
    title: "",
    description: "",
    resource_type: "article",
    resource_url: "",
    category: "General",
    tags: "",
    is_shareable: true
  });

  useEffect(() => {
    fetchResources();
  }, [clinicianId]);

  const fetchResources = async () => {
    const { data, error } = await supabase
      .from("clinician_resources")
      .select("*")
      .eq("clinician_id", clinicianId)
      .order("created_at", { ascending: false });

    if (error) {
      toast({ title: "Error", description: "Failed to load resources", variant: "destructive" });
    } else {
      setResources(data || []);
    }
    setLoading(false);
  };

  const handleAddResource = async () => {
    const { error } = await supabase.from("clinician_resources").insert({
      clinician_id: clinicianId,
      title: newResource.title,
      description: newResource.description,
      resource_type: newResource.resource_type,
      file_url: newResource.resource_url,
      category: newResource.category,
      tags: newResource.tags.split(",").map(t => t.trim()).filter(Boolean),
      is_public: newResource.is_shareable
    });

    if (error) {
      toast({ title: "Error", description: "Failed to add resource", variant: "destructive" });
    } else {
      toast({ title: "Success", description: "Resource added to your library" });
      setShowAddDialog(false);
      setNewResource({
        title: "",
        description: "",
        resource_type: "article",
        resource_url: "",
        category: "General",
        tags: "",
        is_shareable: true
      });
      fetchResources();
    }
  };

  const handleShareResource = async () => {
    if (!selectedResource || !selectedClientId) return;

    const { error } = await supabase.from("shared_resources").insert({
      resource_id: selectedResource.id,
      clinician_id: clinicianId,
      client_id: selectedClientId,
      message: shareMessage || null
    } as any);

    if (error) {
      toast({ title: "Error", description: "Failed to share resource", variant: "destructive" });
    } else {
      toast({ title: "Success", description: "Resource shared with client" });
      setShowShareDialog(false);
      setSelectedResource(null);
      setSelectedClientId("");
      setShareMessage("");
    }
  };

  const handleDeleteResource = async (resourceId: string) => {
    const { error } = await supabase
      .from("clinician_resources")
      .delete()
      .eq("id", resourceId);

    if (error) {
      toast({ title: "Error", description: "Failed to delete resource", variant: "destructive" });
    } else {
      toast({ title: "Success", description: "Resource deleted" });
      fetchResources();
    }
  };

  const filteredResources = resources.filter(r => {
    const matchesSearch = r.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.tags?.some(t => t.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCategory = categoryFilter === "all" || r.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const getResourceIcon = (type: string) => {
    const resourceType = RESOURCE_TYPES.find(t => t.value === type);
    return resourceType?.icon || FileText;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1 max-w-md">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search resources..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {CATEGORIES.map(cat => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button className="gap-2">
              <Plus className="h-4 w-4" />
              Add Resource
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Add New Resource</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Title</Label>
                <Input
                  value={newResource.title}
                  onChange={(e) => setNewResource({ ...newResource, title: e.target.value })}
                  placeholder="Resource title"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={newResource.description}
                  onChange={(e) => setNewResource({ ...newResource, description: e.target.value })}
                  placeholder="Brief description"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Type</Label>
                  <Select value={newResource.resource_type} onValueChange={(v) => setNewResource({ ...newResource, resource_type: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {RESOURCE_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Category</Label>
                  <Select value={newResource.category} onValueChange={(v) => setNewResource({ ...newResource, category: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>URL</Label>
                <Input
                  value={newResource.resource_url}
                  onChange={(e) => setNewResource({ ...newResource, resource_url: e.target.value })}
                  placeholder="https://..."
                />
              </div>
              <div>
                <Label>Tags (comma separated)</Label>
                <Input
                  value={newResource.tags}
                  onChange={(e) => setNewResource({ ...newResource, tags: e.target.value })}
                  placeholder="anxiety, coping, meditation"
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
                <Button onClick={handleAddResource} disabled={!newResource.title || !newResource.resource_url}>
                  Add Resource
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Share Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Share Resource with Client</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Select Client</Label>
              <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(client => (
                    <SelectItem key={client.id} value={client.client_id}>
                      {client.client_profile?.display_name || 'Client'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Message (optional)</Label>
              <Textarea
                value={shareMessage}
                onChange={(e) => setShareMessage(e.target.value)}
                placeholder="Add a personal note..."
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowShareDialog(false)}>Cancel</Button>
              <Button onClick={handleShareResource} disabled={!selectedClientId}>
                Share
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Resources Grid */}
      {filteredResources.length === 0 ? (
        <Card className="bg-card/50 border-border/30">
          <CardContent className="py-12 text-center">
            <FolderOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">No Resources Found</h3>
            <p className="text-muted-foreground mb-4">
              {searchQuery || categoryFilter !== "all" 
                ? "Try adjusting your search or filters"
                : "Start building your resource library for clients"}
            </p>
            {!searchQuery && categoryFilter === "all" && (
              <Button onClick={() => setShowAddDialog(true)} className="gap-2">
                <Plus className="h-4 w-4" />
                Add Your First Resource
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredResources.map(resource => {
            const Icon = getResourceIcon(resource.resource_type);
            return (
              <Card key={resource.id} className="bg-card/50 border-border/30 hover:border-primary/50 transition-colors">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <Icon className="h-4 w-4 text-primary" />
                      </div>
                      <Badge variant="secondary" className="text-xs">{resource.category}</Badge>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => {
                          setSelectedResource(resource);
                          setShowShareDialog(true);
                        }}
                      >
                        <Share2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive"
                        onClick={() => handleDeleteResource(resource.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <CardTitle className="text-base mt-2">{resource.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                    {resource.description}
                  </p>
                  {resource.tags && resource.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {resource.tags.slice(0, 3).map((tag, i) => (
                        <Badge key={i} variant="outline" className="text-xs">{tag}</Badge>
                      ))}
                    </div>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full gap-2"
                    onClick={() => window.open(resource.file_url, '_blank')}
                  >
                    <ExternalLink className="h-4 w-4" />
                    Open Resource
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
